import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, FormField, Input, Modal, Select, Skeleton, StockLevelIndicator, useToast } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { formatNumber } from '@/utils/currency';
import type { UnallocatedStock, VariantLocationStock } from '@/types/product';
import { StockAdjustModal } from './StockAdjustModal';

/**
 * Stock tab of a product with variants (POS comments item 15).
 *
 * The till sells only variants, so the parent's own rows are a container: this
 * lists every variant at every location with its own Adjust, and flags units
 * sitting on the parent (opening stock entered before variants were added,
 * receipts against the parent SKU) — they can't be sold until assigned to a
 * variant.
 */
export function VariantStockPanel({ parentId, unit }: { parentId: string; unit: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [adjusting, setAdjusting] = useState<VariantLocationStock | null>(null);
  const [assigning, setAssigning] = useState<UnallocatedStock | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['product', parentId, 'variant-stock'],
    queryFn: () => productService.getVariantStock(parentId),
  });
  const variants = data?.variants ?? [];
  const unallocated = data?.unallocated ?? [];

  function refresh() {
    // ['product', parentId] prefixes the variant stock, the variants list
    // (Current Stock card) and the history.
    queryClient.invalidateQueries({ queryKey: ['product', parentId] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={56} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {unallocated.map((u) => (
        <div
          key={u.product_id}
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning-200 bg-gradient-to-r from-warning-50 to-danger-50 p-4 shadow-md dark:border-warning-800 dark:from-warning-900/20 dark:to-danger-900/20"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-warning-800 dark:text-warning-200">
              {formatNumber(u.stock_quantity)} {unit} at {u.location_name} not assigned to a variant
            </p>
            <p className="text-xs text-warning-700 dark:text-warning-300">
              The till sells only variants, so these units can't be sold until they are assigned to one.
            </p>
          </div>
          {u.stock_quantity > 0 && (
            <Button variant="primary" size="sm" onClick={() => setAssigning(u)}>
              Assign to variant
            </Button>
          )}
        </div>
      ))}

      {variants.length === 0 ? (
        <div className={cn(GLASS_FORM, 'p-8 text-center')}>
          <p className="font-medium text-neutral-700 dark:text-neutral-200">No variant stock yet</p>
        </div>
      ) : (
        <div className={cn(GLASS_FORM, 'overflow-x-auto')}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-[11px] uppercase tracking-wider text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
                <th className="px-4 py-2.5">Variant</th>
                <th className="px-4 py-2.5">Location</th>
                <th className="px-4 py-2.5 text-right">Stock</th>
                <th className="px-4 py-2.5 text-right">Threshold</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => (
                <tr
                  key={v.product_id}
                  className="animate-slide-up border-b border-neutral-100 transition-colors even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-800 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-700/40"
                >
                  <td className="px-4 py-2.5">
                    <p className={cn('font-medium text-neutral-900 dark:text-white', !v.is_active && 'opacity-60')}>{v.name}</p>
                    <p className="font-mono text-xs text-neutral-500 dark:text-neutral-400">{v.sku}</p>
                  </td>
                  <td className="px-4 py-2.5 text-neutral-700 dark:text-neutral-300">{v.location_name}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-900 dark:text-white">{formatNumber(v.stock_quantity)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-500">{v.low_stock_threshold ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <StockLevelIndicator currentStock={v.stock_quantity} reorderLevel={v.low_stock_threshold ?? undefined} variant="badge" />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="secondary" size="sm" onClick={() => setAdjusting(v)}>
                      Adjust
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adjusting && (
        <StockAdjustModal
          productId={adjusting.product_id}
          title={`Adjust Stock — ${adjusting.name} · ${adjusting.location_name}`}
          stockRows={[adjusting]}
          defaultLocationId={adjusting.location_id}
          onClose={() => setAdjusting(null)}
          onAdjusted={() => {
            setAdjusting(null);
            refresh();
            toast({ title: 'Stock adjusted', variant: 'success' });
          }}
        />
      )}

      {assigning && (
        <AssignStockModal
          parentId={parentId}
          source={assigning}
          variants={variants.filter((v) => v.location_id === assigning.location_id && v.is_active)}
          unit={unit}
          onClose={() => setAssigning(null)}
          onAssigned={() => {
            setAssigning(null);
            refresh();
            toast({ title: 'Stock assigned to variant', variant: 'success' });
          }}
        />
      )}
    </div>
  );
}

function AssignStockModal({
  parentId,
  source,
  variants,
  unit,
  onClose,
  onAssigned,
}: {
  parentId: string;
  source: UnallocatedStock;
  variants: VariantLocationStock[];
  unit: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [variantId, setVariantId] = useState('');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const quantity = Number(qty);
  const tooMany = quantity > source.stock_quantity;

  const assign = useMutation({
    mutationFn: () =>
      productService.assignVariantStock(parentId, {
        variant_id: variantId,
        location_id: source.location_id,
        quantity,
        ...(notes ? { notes } : {}),
      }),
    onSuccess: onAssigned,
    onError: (err: Error) => setError(err.message || 'Failed to assign stock'),
  });

  return (
    <Modal open title={`Assign to variant — ${source.location_name}`} onClose={onClose}>
      <div className="space-y-4 p-4">
        {error && (
          <p role="alert" className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/30 dark:text-danger-300">
            {error}
          </p>
        )}
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {formatNumber(source.stock_quantity)} {unit} unassigned. Move some or all of them to a variant at this location.
        </p>
        <FormField label="Variant" required>
          <Select
            options={variants.map((v) => ({ label: `${v.name} — ${v.stock_quantity} in stock`, value: v.product_id }))}
            value={variantId}
            onChange={(v) => setVariantId(typeof v === 'string' ? v : '')}
            placeholder={variants.length ? 'Select variant' : 'No active variant at this location'}
          />
        </FormField>
        <FormField
          label="Quantity"
          required
          error={tooMany ? `Only ${source.stock_quantity} unassigned` : undefined}
        >
          <Input type="number" value={qty} onChange={setQty} placeholder={`Up to ${source.stock_quantity}`} />
        </FormField>
        <FormField label="Notes">
          <Input value={notes} onChange={setNotes} placeholder="Optional notes" />
        </FormField>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => { setError(''); assign.mutate(); }}
            disabled={!variantId || !(quantity > 0) || tooMany || assign.isPending}
          >
            {assign.isPending ? 'Assigning…' : 'Assign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
